import { useState, useRef, useEffect, useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  customFetch,
  useListCourses,
  type Class,
  type MarkbookAssessment,
  type MarkbookMark,
  type MarkbookView,
  type Student,
  type CreateMarkbookAssessmentInput,
  type UpsertMarkbookMarkInput,
} from "@workspace/api-client-react";
import {
  BookMarked, Plus, Loader2, ChevronDown, X, Search,
} from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  formatStudentName,
  useDefaultStudentDisplayMode,
  type Student as DisplayStudent,
} from "@/lib/studentDisplay";

async function fetchClasses(): Promise<Class[]> {
  return customFetch<Class[]>("/api/markbook/classes");
}
async function fetchMarkbook(classId: number): Promise<MarkbookView> {
  return customFetch<MarkbookView>(`/api/markbook?classId=${classId}`);
}

// ─── Editable mark cell ───────────────────────────────────────────────────────

function MarkCell({
  value, maxMark, onSave,
}: { value: string | null | undefined; maxMark: string; onSave: (v: string) => void }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value ?? "");
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => { if (editing) inputRef.current?.focus(); }, [editing]);
  useEffect(() => { setDraft(value ?? ""); }, [value]);

  const commit = () => { onSave(draft); setEditing(false); };

  const numVal = parseFloat(draft);
  const maxNum = parseFloat(maxMark);
  const pct = !isNaN(numVal) && !isNaN(maxNum) && maxNum > 0 ? Math.round((numVal / maxNum) * 100) : null;
  const colour =
    pct === null ? "bg-white" :
    pct >= 85 ? "bg-green-50" :
    pct >= 60 ? "bg-amber-50" :
    "bg-red-50";

  if (editing) {
    return (
      <td className="border border-gray-200 p-0 min-w-[80px]">
        <input
          ref={inputRef}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={commit}
          onKeyDown={(e) => { if (e.key === "Enter") commit(); if (e.key === "Escape") { setDraft(value ?? ""); setEditing(false); } }}
          className="w-full h-full px-2 py-1.5 text-sm text-center outline-none bg-primary/5 border-2 border-primary rounded"
        />
      </td>
    );
  }

  return (
    <td
      className={`border border-gray-200 min-w-[80px] cursor-pointer hover:bg-primary/5 transition-colors ${colour}`}
      onClick={() => setEditing(true)}
      data-write-action
    >
      <div className="px-2 py-1.5 text-center">
        {draft ? (
          <span className="text-sm font-medium text-gray-800">{draft}</span>
        ) : (
          <span className="text-gray-300 text-xs">—</span>
        )}
      </div>
    </td>
  );
}

// ─── Status filter options ─────────────────────────────────────────────────────

type StatusFilter = "all" | "missing" | "has";

const STATUS_OPTIONS: { value: StatusFilter; label: string }[] = [
  { value: "all", label: "All" },
  { value: "missing", label: "Missing marks" },
  { value: "has", label: "Has marks" },
];

// ─── Main page ────────────────────────────────────────────────────────────────

export default function Markbook() {
  const qc = useQueryClient();
  const accountDisplayMode = useDefaultStudentDisplayMode();
  const [selectedClassId, setSelectedClassId] = useState<number | null>(null);
  const [selectedCourseId, setSelectedCourseId] = useState<number | null>(null);
  const [showAddAssessment, setShowAddAssessment] = useState(false);
  const [newAssessmentName, setNewAssessmentName] = useState("");
  const [newMaxMark, setNewMaxMark] = useState("100");

  // Filters
  const [studentSearch, setStudentSearch] = useState("");
  const [assessmentSearch, setAssessmentSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");

  const { data: classes = [], isLoading: classesLoading } = useQuery<Class[]>({
    queryKey: ["markbook", "classes"],
    queryFn: fetchClasses,
  });

  // Subjects (courses) — used for the optional subject pre-filter
  const { data: courses = [] } = useListCourses();

  // Classes visible given the optional subject filter.
  // Class.courseIds lists every subject linked to that class.
  const visibleClasses = useMemo(
    () =>
      selectedCourseId === null
        ? classes
        : classes.filter((c) => c.courseIds.includes(selectedCourseId)),
    [classes, selectedCourseId],
  );

  const selectedCourse = courses.find((c) => c.id === selectedCourseId) ?? null;

  const { data, isLoading: bookLoading } = useQuery<MarkbookView>({
    queryKey: ["markbook", selectedClassId],
    queryFn: () => fetchMarkbook(selectedClassId!),
    enabled: !!selectedClassId,
  });

  // Auto-select first class (only when no subject filter is active)
  useEffect(() => {
    if (!selectedClassId && classes.length > 0 && selectedCourseId === null) {
      setSelectedClassId(classes[0].id);
    }
  }, [classes, selectedClassId, selectedCourseId]);

  // When the subject filter changes, clear the class selection if it is no longer visible
  useEffect(() => {
    if (selectedClassId !== null && !visibleClasses.some((c) => c.id === selectedClassId)) {
      setSelectedClassId(null);
    }
  }, [visibleClasses, selectedClassId]);

  // Reset filters when class changes
  useEffect(() => {
    setStudentSearch("");
    setAssessmentSearch("");
    setStatusFilter("all");
  }, [selectedClassId]);

  const addAssessment = useMutation({
    mutationFn: (body: CreateMarkbookAssessmentInput) =>
      customFetch<MarkbookAssessment>("/api/markbook/assessments", {
        method: "POST",
        body: JSON.stringify(body),
        headers: { "Content-Type": "application/json" },
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["markbook", selectedClassId] });
      setShowAddAssessment(false);
      setNewAssessmentName("");
      setNewMaxMark("100");
    },
  });

  const deleteAssessment = useMutation({
    mutationFn: (id: number) =>
      customFetch<{ ok: true }>(`/api/markbook/assessments/${id}`, { method: "DELETE" }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["markbook", selectedClassId] }),
  });

  const saveMark = useMutation({
    mutationFn: (body: UpsertMarkbookMarkInput) =>
      customFetch<MarkbookMark>("/api/markbook/marks", {
        method: "PUT",
        body: JSON.stringify(body),
        headers: { "Content-Type": "application/json" },
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["markbook", selectedClassId] }),
  });

  const getMarkValue = (studentId: number, assessmentId: number): string | null => {
    return (
      data?.marks.find(
        (m) => m.studentId === studentId && m.assessmentId === assessmentId,
      )?.mark ?? null
    );
  };

  const selectedClass = classes.find((c) => c.id === selectedClassId);

  // ── Derived filtered lists ──────────────────────────────────────────────────

  // TODO: Assessment-level subject filtering is not yet possible because
  // MarkbookAssessment has no courseId field. The markbook fetch remains
  // class-based even when a subject is selected. To enable true subject
  // filtering of assessments, the API would need to return a courseId (or
  // similar) on each MarkbookAssessment so they can be tagged by subject.
  const filteredAssessments = useMemo(() => {
    if (!data) return [];
    const q = assessmentSearch.trim().toLowerCase();
    if (!q) return data.assessments;
    return data.assessments.filter((a) => a.name.toLowerCase().includes(q));
  }, [data, assessmentSearch]);

  const filteredStudents = useMemo(() => {
    if (!data) return [];
    const q = studentSearch.trim().toLowerCase();
    let students = data.students;

    if (q) {
      students = students.filter((s) => {
        const display = formatStudentName(s as DisplayStudent, accountDisplayMode).toLowerCase();
        const legal = ((s as any).legalName ?? "").toLowerCase();
        const preferred = ((s as any).preferredName ?? "").toLowerCase();
        const code = ((s as any).code ?? "").toLowerCase();
        return display.includes(q) || legal.includes(q) || preferred.includes(q) || code.includes(q);
      });
    }

    if (statusFilter !== "all") {
      students = students.filter((s) =>
        filteredAssessments.some((a) => {
          const mark = data.marks.find(
            (m) => m.studentId === s.id && m.assessmentId === a.id,
          )?.mark ?? null;
          return statusFilter === "missing"
            ? (mark == null || mark === "")
            : (mark != null && mark !== "");
        }),
      );
    }

    return students;
  }, [data, studentSearch, assessmentSearch, statusFilter, filteredAssessments, accountDisplayMode]);

  const isFiltered =
    studentSearch.trim() !== "" ||
    assessmentSearch.trim() !== "" ||
    statusFilter !== "all";

  function clearFilters() {
    setStudentSearch("");
    setAssessmentSearch("");
    setStatusFilter("all");
  }

  return (
    <div className="p-4 sm:p-6 max-w-7xl mx-auto space-y-5">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-2">
          <BookMarked className="h-5 w-5 text-primary" />
          <h1 className="text-2xl font-bold text-gray-900">Markbook</h1>
        </div>

        <div className="flex items-center gap-2">
          {/* Subject filter (optional) — narrows the class picker */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant={selectedCourseId !== null ? "secondary" : "outline"}
                className="gap-2 min-w-[140px] justify-between"
              >
                <span className="truncate">{selectedCourse?.name ?? "All subjects"}</span>
                <ChevronDown className="h-4 w-4 shrink-0" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuItem onClick={() => setSelectedCourseId(null)}>
                All subjects
              </DropdownMenuItem>
              {courses.length > 0 && <DropdownMenuSeparator />}
              {courses.length === 0 ? (
                <div className="p-3 text-sm text-gray-400 text-center">No subjects yet</div>
              ) : (
                courses.map((course) => (
                  <DropdownMenuItem key={course.id} onClick={() => setSelectedCourseId(course.id)}>
                    <span
                      className="mr-2 inline-block h-2 w-2 rounded-full shrink-0"
                      style={{ backgroundColor: course.color }}
                    />
                    {course.name}
                  </DropdownMenuItem>
                ))
              )}
            </DropdownMenuContent>
          </DropdownMenu>

          {/* Class picker — filtered by selected subject when one is active */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" className="gap-2 min-w-[160px] justify-between">
                <span className="truncate">{selectedClass?.name ?? "Select class"}</span>
                <ChevronDown className="h-4 w-4 shrink-0" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              {classesLoading ? (
                <div className="p-3 text-center"><Loader2 className="h-4 w-4 animate-spin mx-auto text-primary" /></div>
              ) : visibleClasses.length === 0 ? (
                <div className="p-3 text-sm text-gray-400 text-center">
                  {selectedCourseId !== null ? "No classes linked to this subject" : "No classes yet"}
                </div>
              ) : (
                visibleClasses.map((c) => (
                  <DropdownMenuItem key={c.id} onClick={() => setSelectedClassId(c.id)}>
                    {c.name}
                    {c.yearLevel && <span className="text-xs text-gray-400 ml-1">({c.yearLevel})</span>}
                  </DropdownMenuItem>
                ))
              )}
            </DropdownMenuContent>
          </DropdownMenu>

          <Button
            size="sm"
            onClick={() => setShowAddAssessment(true)}
            disabled={!selectedClassId}
            className="gap-1.5"
           data-write-action>
            <Plus className="h-4 w-4" /> Add assessment
          </Button>
        </div>
      </div>

      {/* Filter bar — only when a class is loaded */}
      {data && !bookLoading && (
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative flex-1 min-w-[160px] max-w-[220px]">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400 pointer-events-none" />
            <Input
              value={studentSearch}
              onChange={(e) => setStudentSearch(e.target.value)}
              placeholder="Filter students…"
              className="pl-8 h-8 text-sm"
            />
          </div>

          <div className="relative flex-1 min-w-[160px] max-w-[220px]">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400 pointer-events-none" />
            <Input
              value={assessmentSearch}
              onChange={(e) => setAssessmentSearch(e.target.value)}
              placeholder="Filter assessments…"
              className="pl-8 h-8 text-sm"
            />
          </div>

          <div className="flex items-center gap-1 rounded-lg border border-gray-200 bg-white p-0.5">
            {STATUS_OPTIONS.map((opt) => (
              <button
                key={opt.value}
                onClick={() => setStatusFilter(opt.value)}
                className={`px-2.5 py-1 rounded-md text-xs font-medium transition-colors whitespace-nowrap ${
                  statusFilter === opt.value
                    ? "bg-primary text-white shadow-sm"
                    : "text-gray-500 hover:text-gray-800"
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>

          {isFiltered && (
            <Button
              variant="ghost"
              size="sm"
              onClick={clearFilters}
              className="h-8 gap-1.5 text-gray-500 hover:text-gray-800 text-xs"
            >
              <X className="h-3.5 w-3.5" />
              Clear filters
            </Button>
          )}
        </div>
      )}

      {/* No class selected */}
      {!selectedClassId && !classesLoading && (
        <Card>
          <CardContent className="py-16 text-center space-y-3">
            <BookMarked className="h-10 w-10 text-gray-300 mx-auto" />
            <div>
              <p className="font-medium text-gray-600">No classes yet</p>
              <p className="text-sm text-gray-400 mt-1">Markbook uses Classes because marks are attached to student groups. Create a class to get started.</p>
            </div>
            <Button variant="outline" onClick={() => window.location.href = "/app/classes"}>Go to Classes</Button>
          </CardContent>
        </Card>
      )}

      {/* Loading */}
      {selectedClassId && bookLoading && (
        <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
      )}

      {/* Grid */}
      {data && !bookLoading && (
        <>
          {data.assessments.length === 0 && data.students.length === 0 && (
            <Card>
              <CardContent className="py-12 text-center space-y-2">
                <p className="text-gray-500">No students or assessments yet for <strong>{data.class.name}</strong>.</p>
                <p className="text-sm text-gray-400">Add students to this class, then use <strong>Add assessment</strong> to create columns.</p>
              </CardContent>
            </Card>
          )}

          {data.students.length === 0 && data.assessments.length > 0 && (
            <Card>
              <CardContent className="py-8 text-center text-sm text-gray-500">
                No students in <strong>{data.class.name}</strong> yet. Add students via the Classes page.
              </CardContent>
            </Card>
          )}

          {/* Filtered-empty state */}
          {data.students.length > 0 && isFiltered && filteredStudents.length === 0 && (
            <Card>
              <CardContent className="py-10 text-center space-y-2">
                <p className="text-gray-500 font-medium">No markbook entries match these filters.</p>
                <button
                  onClick={clearFilters}
                  className="text-sm text-primary underline underline-offset-2 hover:text-primary/80"
                >
                  Clear filters
                </button>
              </CardContent>
            </Card>
          )}

          {data.students.length > 0 && filteredStudents.length > 0 && (
            <div className="overflow-x-auto rounded-xl border border-gray-200 shadow-sm">
              <table className="w-full text-sm border-collapse bg-white">
                <thead>
                  <tr className="bg-gray-50">
                    <th className="border border-gray-200 px-3 py-2.5 text-left font-semibold text-gray-700 sticky left-0 bg-gray-50 z-10 min-w-[160px]">
                      Student
                    </th>
                    {filteredAssessments.map((a) => (
                      <th key={a.id} className="border border-gray-200 px-2 py-2.5 text-center font-medium text-gray-700 min-w-[90px] group">
                        <div className="flex items-center justify-center gap-1">
                          <div>
                            <div className="text-xs font-semibold text-gray-800 truncate max-w-[100px]">{a.name}</div>
                            <div className="text-xs text-gray-400 font-normal">/{a.maxMark}</div>
                          </div>
                          <button
                            onClick={() => deleteAssessment.mutate(a.id)}
                            className="opacity-0 group-hover:opacity-100 transition-opacity ml-1 text-gray-400 hover:text-red-500"
                           data-write-action>
                            <X className="h-3 w-3" />
                          </button>
                        </div>
                      </th>
                    ))}
                    {filteredAssessments.length === 0 && (
                      <th className="border border-gray-200 px-3 py-2.5 text-center text-gray-400 font-normal text-xs italic">
                        {assessmentSearch.trim()
                          ? "No assessments match this search"
                          : "Add an assessment to start recording marks"}
                      </th>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {filteredStudents.map((s, i) => (
                    <tr key={s.id} className={i % 2 === 0 ? "bg-white" : "bg-gray-50/50"}>
                      <td className="border border-gray-200 px-3 py-2 sticky left-0 bg-inherit z-10 font-medium text-gray-800">
                        {formatStudentName(s as DisplayStudent, accountDisplayMode)}
                      </td>
                      {filteredAssessments.map((a) => (
                        <MarkCell
                          key={a.id}
                          value={getMarkValue(s.id, a.id)}
                          maxMark={a.maxMark}
                          onSave={(mark) => saveMark.mutate({ assessmentId: a.id, studentId: s.id, mark: mark.trim() || null })}
                        />
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Summary row — uses full data, not filtered, so averages stay accurate */}
          {data.students.length > 0 && data.assessments.length > 0 && (
            <div className="flex gap-4 flex-wrap">
              {data.assessments.map((a) => {
                const marks = data.students
                  .map((s) => parseFloat(getMarkValue(s.id, a.id) ?? ""))
                  .filter((v) => !isNaN(v));
                const avg = marks.length ? (marks.reduce((s, v) => s + v, 0) / marks.length).toFixed(1) : null;
                return (
                  <div key={a.id} className="text-xs text-gray-500">
                    <span className="font-medium text-gray-700">{a.name}</span>: {avg ? `avg ${avg}/${a.maxMark}` : "no marks yet"}
                    <span className="text-gray-400 ml-1">({marks.length}/{data.students.length} entered)</span>
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}

      {/* Add assessment dialog */}
      <Dialog open={showAddAssessment} onOpenChange={setShowAddAssessment}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader><DialogTitle>Add assessment</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label>Assessment name</Label>
              <Input
                value={newAssessmentName}
                onChange={(e) => setNewAssessmentName(e.target.value)}
                placeholder="e.g. Term 1 Test, Essay 1…"
                className="mt-1.5"
                autoFocus
                onKeyDown={(e) => { if (e.key === "Enter" && newAssessmentName.trim() && selectedClassId !== null) addAssessment.mutate({ classId: selectedClassId, name: newAssessmentName, maxMark: newMaxMark }); }}
              />
            </div>
            <div>
              <Label>Out of</Label>
              <Input
                value={newMaxMark}
                onChange={(e) => setNewMaxMark(e.target.value)}
                placeholder="100"
                className="mt-1.5 w-28"
                type="number"
                min="1"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddAssessment(false)}>Cancel</Button>
            <Button
              disabled={!newAssessmentName.trim() || addAssessment.isPending || selectedClassId === null}
              onClick={() => { if (selectedClassId !== null) addAssessment.mutate({ classId: selectedClassId, name: newAssessmentName, maxMark: newMaxMark }); }}
              className="gap-1.5"
             data-write-action>
              {addAssessment.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
              Add
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
